// Food.jsx
import React from 'react';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';

// === IMAGES ===
// Replace with your actual food / dining-themed hero image
import heroBg from '../assets/Food/rest10.webp';  // ← change to your real image path
import ayurvedicFoodImage1 from '../assets/River/27.jpeg'; // ← change to your real image path
import ayurvedicFoodImage2 from '../assets/Food/Dining-1284X595.jpg'; // ← change to your real image path
import ayuFood from '../assets/Food/medical_img08.jpg'; // ← change to your real image path
import ayuFood2 from '../assets/Food/Thaulle-Resort-Sri-Lanka-Thaulle-Cuisine-Daily-Menu.jpg'; // ← additional image for nutrition section
import restaurantImg from '../assets/River/26.jpeg'; // ← change to your real image path

// === MOTION VARIANTS ===
const sectionVariants = {
    hidden: { opacity: 0, y: 50 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.8, ease: "easeOut" } },
};

const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
        opacity: 1,
        transition: {
            staggerChildren: 0.25,
            delayChildren: 0.2,
        },
    },
};

const itemVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: {
        opacity: 1,
        y: 0,
        transition: { duration: 0.7, ease: 'easeOut' },
    },
};

const imageVariants = {
    hidden: { opacity: 0, scale: 0.92 },
    visible: {
        opacity: 1,
        scale: 1,
        transition: { duration: 0.9, ease: 'easeOut' },
    },
};

const Food2 = () => {
    const { t } = useTranslation('food2');
    const ayurvedicFood = [
        t('philosophyText1'),
        t('philosophyText2'),
        t('philosophyText3'),
        t('philosophyText4'),
        t('philosophyText5'),
        t('philosophyText6'),
        t('philosophyText7'),
        t('philosophyText8')
    ];
    const restaurantDetails = [
        t('restaurantDetail1'),
        t('restaurantDetail2'),
        t('restaurantDetail3'),
        t('restaurantDetail4'),
        t('restaurantDetail5')
    ];


    return (
        <div className="bg-[#1F1F1F] min-h-screen">
            {/* ===== HERO SECTION ===== */}
            <motion.div
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, amount: 0.3 }}
                variants={sectionVariants}
                className="relative flex h-[60vh] w-full items-center justify-center font-lato md:h-[70vh] lg:h-[85vh]"
            >
                <img
                    src={heroBg}
                    alt="Culinary Experience at Muthumuni Ayurveda River Resort, Aluthgama"
                    className="absolute inset-0 h-full w-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-b from-[#020202c7] via-[#00000080] to-[#42424259]" />

                <div className="container relative z-10 flex items-center justify-center pt-16 md:pt-20">
                    <div className="w-full max-w-4xl text-center">
                        <h1 className="text-5xl md:text-6xl lg:text-7xl font-semibold leading-tight text-white font-copperplate">
                            {t('heroTitle')}
                        </h1>
                        <p className="mt-6 text-lg md:text-2xl lg:text-3xl font-semibold tracking-wide text-[#96BA33] font-lato">
                            {t('heroSubtitle')}
                        </p>


                        {/* Optional decorative SVG lines – remove if you don't want them */}
                        <div className="absolute left-8 top-10 hidden lg:block">
                            <svg width="289" height="245" viewBox="0 0 289 245" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <path d="M289 1H1V244.5" stroke="white" strokeWidth="2" />
                            </svg>
                        </div>
                        <div className="absolute -bottom-12 right-8 hidden lg:block">
                            <svg width="289" height="244" viewBox="0 0 289 244" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <path d="M0 243.5H288V0" stroke="white" strokeWidth="2" />
                            </svg>
                        </div>
                    </div>
                </div>
            </motion.div>
            {/* ===== AYURVEDIC FOOD / WELLNESS SECTION ===== */}
            <section className="container mx-auto px-4 my-8 md:my-10 lg:my-12">

                {/* === MOBILE / SM layout (stacked) === */}
                <div className="block md:hidden space-y-10">
                    {/* Eyebrow + Title */}
                    <div className="space-y-5 text-center">
                        <div className="flex items-center justify-center gap-3">
                            <div className="h-1 w-12 bg-gradient-to-r from-[#96BA33] to-[#BF943A]"></div>
                            <span className="text-[#96BA33] font-semibold tracking-widest text-sm uppercase">
                                {t('wellnessEyebrow')}
                            </span>
                        </div>

                        <h2 className="font-bold text-white text-3xl sm:text-4xl leading-tight">
                            {t('wellnessTitle')} <span className="text-[#96BA33]">{t('ayurvedicFood')}</span>
                        </h2>
                    </div>

                    {/* Overlapping Images */}
                    <div className="relative pb-28 sm:pb-32">
                        <div className="absolute inset-0 bg-gradient-to-br from-[#96BA33]/20 to-[#BF943A]/20 rounded-2xl blur-xl"></div>

                        <div className="relative z-10">
                            <img
                                src={ayurvedicFoodImage1}
                                alt={t('foodImage1Alt')}
                                className="w-full rounded-2xl shadow-2xl object-cover"
                            />

                            <img
                                src={ayurvedicFoodImage2}
                                alt={t('foodImage2Alt')}
                                className="absolute -bottom-24 sm:-bottom-28 left-1/2 -translate-x-1/2 w-[70%] sm:w-[65%] rounded-2xl shadow-2xl object-cover border-4 border-[#96BA33] z-20"
                            />
                        </div>
                    </div>

                    {/* Descriptions */}
                    <div className="space-y-6">
                        <p className="text-base sm:text-lg text-white leading-relaxed border-l-4 border-[#96BA33] pl-5 py-4 bg-[#2a2a2a] rounded-r-lg">
                            {t('wellnessIntro1')}
                        </p>
                        <p className="text-base sm:text-lg text-white leading-relaxed border-l-4 border-[#BF943A] pl-5 py-4 bg-[#2a2a2a] rounded-r-lg">
                            {t('wellnessIntro2')}
                        </p>
                        <p className="text-base sm:text-lg text-white leading-relaxed border-l-4 border-[#96BA33] pl-5 py-4 bg-[#2a2a2a] rounded-r-lg">
                            {t('wellnessIntro3')}
                        </p>
                    </div>
                </div>

                {/* === DESKTOP / MD+ layout (side-by-side with overlapping images) === */}
                <motion.div
                    initial="hidden"
                    whileInView="visible"
                    viewport={{ once: true, amount: 0.25 }}
                    variants={containerVariants}
                    className="hidden md:flex flex-col md:flex-row items-center gap-10 lg:gap-16"
                >
                    {/* Left side - Text */}
                    <div className="w-full md:w-3/5 space-y-6">
                        <motion.div variants={itemVariants} className="flex items-center gap-3">
                            <div className="h-1 w-16 bg-gradient-to-r from-[#96BA33] to-[#BF943A]"></div>
                            <span className="text-[#96BA33] font-semibold tracking-widest text-sm uppercase">
                                {t('wellnessEyebrow')}
                            </span>
                        </motion.div>

                        <motion.h2
                            variants={itemVariants}
                            className="font-bold text-white text-3xl lg:text-[3.5rem] leading-tight"
                        >
                            {t('wellnessTitle')} <span className="text-[#96BA33]">{t('ayurvedicFood')}</span>
                        </motion.h2>

                        <motion.p
                            variants={itemVariants}
                            className="text-lg lg:text-xl text-white leading-relaxed border-l-4 border-[#96BA33] pl-6 py-4 bg-[#2a2a2a] rounded-r-lg"
                        >
                            {t('wellnessIntro1')}
                        </motion.p>

                        <motion.p
                            variants={itemVariants}
                            className="text-lg lg:text-xl text-white leading-relaxed border-l-4 border-[#BF943A] pl-6 py-4 bg-[#2a2a2a] rounded-r-lg"
                        >
                            {t('wellnessIntro2')}
                        </motion.p>

                        <motion.p
                            variants={itemVariants}
                            className="text-lg lg:text-xl text-white leading-relaxed border-l-4 border-[#96BA33] pl-6 py-4 bg-[#2a2a2a] rounded-r-lg"
                        >
                            {t('wellnessIntro3')}
                        </motion.p>
                    </div>

                    {/* Right side - Overlapping images */}
                    <motion.div
                        variants={imageVariants}
                        className="w-full md:w-2/5 relative"
                    >
                        <div className="relative">
                            <motion.div
                                className="absolute -inset-6 bg-gradient-to-br from-[#96BA33] to-[#BF943A] rounded-2xl opacity-20 blur-xl"
                            ></motion.div>

                            <motion.img
                                variants={imageVariants}
                                src={ayurvedicFoodImage1}
                                alt={t('foodImage1Alt')}
                                className="w-full md:w-[90%] rounded-2xl shadow-2xl object-cover relative z-10 hover:shadow-[0_20px_60px_rgba(150,186,51,0.3)] transition-all duration-500"
                            />

                            <motion.img
                                variants={imageVariants}
                                src={ayurvedicFoodImage2}
                                alt={t('foodImage2Alt')}
                                className="absolute bottom-[-100px] left-[40%] w-[65%] rounded-2xl shadow-2xl object-cover z-20 border-4 border-[#96BA33] hover:shadow-[0_20px_60px_rgba(150,186,51,0.4)] transition-all duration-500"
                            />
                        </div>
                    </motion.div>
                </motion.div>
            </section>
            {/* ===== AYURVEDIC NUTRITION SECTION ===== */}
            <motion.section
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, amount: 0.3 }}
                variants={sectionVariants}
                className="bg-gradient-to-b from-[#1F1F1F] via-[#252525] to-[#1F1F1F] py-16 md:py-24"
            >
                <div className="container mx-auto px-6 lg:px-8">
                    <motion.div
                        variants={itemVariants}
                        className="text-center mb-12"
                    >
                        <div className="inline-block mb-4">
                            <span className="text-[#96BA33] font-semibold tracking-widest text-sm uppercase">{t('culinaryPhilosophyEyebrow')}</span>
                        </div>
                        <h2 className="text-4xl md:text-5xl font-bold text-white mb-4">
                            {t('culinaryPhilosophyTitle')} <span className="text-[#96BA33]">{t('nutritionHighlight')}</span> {t('culinaryPhilosophyResort')}
                        </h2>
                        <div className="h-1 w-20 mx-auto bg-gradient-to-r from-[#96BA33] to-[#BF943A]"></div>
                    </motion.div>

                    <div className="flex flex-col lg:flex-row items-center gap-10 lg:gap-16">
                        <motion.div
                            variants={imageVariants}
                            className="w-full lg:w-1/2 flex flex-col gap-8"
                        >
                            {/* Top Image */}
                            <div className="relative group">
                                <div className="absolute -inset-8 bg-gradient-to-br from-[#96BA33] via-[#BF943A] to-transparent rounded-3xl opacity-25 blur-2xl group-hover:opacity-40 transition duration-500"></div>
                                <img
                                    src={ayuFood}
                                    alt="Ayurvedic Cuisine at Muthumuni Ayurveda River Resort"
                                    className="w-full h-auto object-cover rounded-3xl shadow-2xl transform group-hover:scale-105 transition duration-500 relative z-10"
                                    loading="lazy"
                                />
                                <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-[#000000dd] to-transparent rounded-b-3xl p-6 z-20">
                                    <p className="text-[#96BA33] font-semibold text-lg">Fresh & Nourishing</p>
                                </div>
                            </div>

                            {/* Bottom Image */}
                            <div className="relative group">
                                <div className="absolute -inset-8 bg-gradient-to-br from-[#BF943A] via-[#96BA33] to-transparent rounded-3xl opacity-25 blur-2xl group-hover:opacity-40 transition duration-500"></div>
                                <img
                                    src={ayuFood2}
                                    alt="Healthy Ayurvedic Ingredients and Spices"
                                    className="w-full h-auto object-cover rounded-3xl shadow-2xl transform group-hover:scale-105 transition duration-500 relative z-10"
                                    loading="lazy"
                                />
                                <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-[#000000dd] to-transparent rounded-b-3xl p-6 z-20">
                                    <p className="text-[#BF943A] font-semibold text-lg">Healing Ingredients</p>
                                </div>
                            </div>
                        </motion.div>

                        <div className="w-full lg:w-1/2 space-y-6 text-white">
                            {ayurvedicFood.map((paragraph, i) => (
                                <motion.div
                                    key={i}
                                    variants={itemVariants}
                                    className="group"
                                >
                                    <div className="flex gap-4">
                                        <div className="flex-shrink-0 w-12 h-12 rounded-full bg-gradient-to-br from-[#96BA33] to-[#BF943A] flex items-center justify-center text-white font-bold text-sm">
                                            {i + 1}
                                        </div>
                                        <p className="text-base md:text-lg lg:text-lg leading-relaxed font-inknut pt-1 group-hover:text-[#96BA33] transition duration-300">
                                            {paragraph}
                                        </p>
                                    </div>
                                </motion.div>
                            ))}
                        </div>
                    </div>
                </div>
            </motion.section>

            <motion.section
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, amount: 0.25 }}
                variants={sectionVariants}
                className="bg-gradient-to-b from-[#1F1F1F] via-[#252525] to-[#1F1F1F] py-12 md:py-16 lg:py-24 relative overflow-hidden"
            >
                {/* Decorative background blobs - smaller on mobile */}
                <div className="absolute top-0 right-0 w-64 sm:w-96 h-64 sm:h-96 bg-[#96BA33] opacity-5 rounded-full blur-3xl"></div>
                <div className="absolute bottom-0 left-0 w-64 sm:w-96 h-64 sm:h-96 bg-[#BF943A] opacity-5 rounded-full blur-3xl"></div>

                <div className="container mx-auto px-5 sm:px-6 lg:px-8 relative z-10">

                    {/* === MOBILE / SM layout (stacked) === */}
                    <div className="block md:hidden space-y-10">
                        {/* Eyebrow */}
                        <div className="text-center">
                            <span className="text-[#96BA33] font-semibold tracking-widest text-sm uppercase">
                                {t('diningExperienceEyebrow')}
                            </span>
                        </div>

                        {/* Title */}
                        <h2 className="text-3xl sm:text-4xl font-bold text-white text-center">
                            {t('restaurantTitlePrefix')}{" "}
                            <span className="text-[#BF943A]">{t('ayurvedicHighlight')}</span>{" "}
                            {t('restaurantTitleSuffix')}
                        </h2>
                        <div className="h-1 w-20 mx-auto bg-gradient-to-r from-[#BF943A] to-[#96BA33]"></div>

                        {/* Image */}
                        <motion.div
                            variants={imageVariants}
                            className="relative"
                        >
                            <div className="absolute -inset-6 bg-gradient-to-br from-[#BF943A] via-[#96BA33] to-transparent rounded-3xl opacity-20 blur-2xl"></div>
                            <div className="relative group">
                                <img
                                    src={restaurantImg}
                                    alt="Dining at Muthumuni Ayurveda River Resort"
                                    className="w-full h-auto object-cover rounded-3xl shadow-2xl border-4 border-[#96BA33]"
                                    loading="lazy"
                                />
                                <div className=" rounded-3xl  flex items-end justify-start p-6 sm:p-8">
                                    <div>
                                        <p className="text-[#BF943A] font-semibold text-lg sm:text-xl mb-2">
                                            {t('premiumDiningLabel')}
                                        </p>
                                        <p className="text-white text-sm sm:text-base max-w-xs">
                                            {t('premiumDiningDescription')}
                                        </p>
                                    </div>
                                </div>
                            </div>
                        </motion.div>

                        {/* Details list */}
                        <motion.div
                            variants={containerVariants}
                            className="space-y-5"
                        >
                            {restaurantDetails.map((point, i) => (
                                <motion.div
                                    key={i}
                                    variants={itemVariants}
                                    className="bg-[#2a2a2a] p-5 sm:p-6 rounded-lg border-l-4 border-[#96BA33] hover:border-[#BF943A] hover:bg-[#323232] transition-all duration-300"
                                >
                                    <div className="flex items-start gap-4">
                                        <div className="flex-shrink-0 w-9 h-9 sm:w-10 sm:h-10 rounded-full bg-gradient-to-br from-[#96BA33] to-[#BF943A] flex items-center justify-center text-white font-bold text-base sm:text-lg">
                                            ✓
                                        </div>
                                        <p className="font-inknut text-base sm:text-lg">
                                            {point}
                                        </p>
                                    </div>
                                </motion.div>
                            ))}
                        </motion.div>
                    </div>

                    {/* === DESKTOP / MD+ layout (side-by-side) === */}
                    <div className="hidden md:block">
                        <motion.div
                            variants={itemVariants}
                            className="text-center mb-12 lg:mb-16"
                        >
                            <div className="inline-block mb-4">
                                <span className="text-[#96BA33] font-semibold tracking-widest text-sm uppercase">
                                    {t('diningExperienceEyebrow')}
                                </span>
                            </div>
                            <h2 className="text-4xl lg:text-5xl font-bold text-white">
                                {t('restaurantTitlePrefix')}{" "}
                                <span className="text-[#BF943A]">{t('ayurvedicHighlight')}</span>{" "}
                                {t('restaurantTitleSuffix')}
                            </h2>
                            <div className="h-1 w-20 mx-auto mt-6 bg-gradient-to-r from-[#BF943A] to-[#96BA33]"></div>
                        </motion.div>

                        <div className="grid md:grid-cols-2 gap-10 lg:gap-16 items-center">
                            {/* Left - Details list */}
                            <motion.div
                                variants={containerVariants}
                                className="space-y-6 text-white text-lg lg:text-xl leading-relaxed"
                            >
                                {restaurantDetails.map((point, i) => (
                                    <motion.div
                                        key={i}
                                        variants={itemVariants}
                                        className="group"
                                    >
                                        <div className="bg-[#2a2a2a] p-6 rounded-lg border-l-4 border-[#96BA33] hover:border-[#BF943A] hover:bg-[#323232] transition-all duration-300 transform hover:translate-x-2">
                                            <div className="flex items-start gap-4">
                                                <div className="flex-shrink-0 w-10 h-10 rounded-full bg-gradient-to-br from-[#96BA33] to-[#BF943A] flex items-center justify-center text-white font-bold text-lg mt-1">
                                                    ✓
                                                </div>
                                                <p className="font-inknut text-base lg:text-lg group-hover:text-[#96BA33] transition duration-300">
                                                    {point}
                                                </p>
                                            </div>
                                        </div>
                                    </motion.div>
                                ))}
                            </motion.div>

                            {/* Right - Image */}
                            <motion.div
                                variants={imageVariants}
                                className="relative"
                            >
                                <div className="absolute -inset-8 bg-gradient-to-br from-[#BF943A] via-[#96BA33] to-transparent rounded-3xl opacity-20 blur-2xl group-hover:opacity-30 transition duration-500"></div>
                                <div className="relative group">
                                    <img
                                        src={restaurantImg}
                                        alt="Dining at Muthumuni Ayurveda River Resort"
                                        className="w-full h-auto object-cover rounded-3xl shadow-2xl transform group-hover:scale-105 transition duration-500 relative z-10 border-4 border-[#96BA33]"
                                        loading="lazy"
                                    />
                                    <div className="absolute inset-0 rounded-3xl bg-gradient-to-t from-[#000000bb] via-transparent to-transparent flex items-end justify-start p-8 z-20">
                                        <div>
                                            <p className="text-[#BF943A] font-semibold text-xl mb-2">
                                                {t('premiumDiningLabel')}
                                            </p>
                                            <p className="text-white text-base max-w-xs">
                                                {t('premiumDiningDescription')}
                                            </p>
                                        </div>
                                    </div>
                                </div>
                            </motion.div>
                        </div>
                    </div>
                </div>
            </motion.section>
        </div >
    );
};

export default Food2;